import {
  ICellRendererParams,
  RowNode,
  ValueSetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject } from "react";

import {
  OrderDetailFormRowValues,
  OrderDetailFormValues,
} from "../../model/form/OrderDetailForm";
import {
  AgDatePicker,
  agNumberFormatter,
  agNumberSetter,
  agProductNameFormatter,
  agYmdFormatter,
} from "../../util/AgGridUtil";
import { OrderStatus, ProductCodeConst } from "../../util/Constant";
import { isEnglish } from "../../util/ecUtil";
import { OrderDetailPermission } from "./OrderDetailPermission";
import {
  TranslateFn,
  createDetailNumberFormatter,
  editableCellRule,
  errorCellRule,
  isEmptyOrderRow,
  renderDetailDeleteButton,
} from "./OrderQuotationUtil";

/** 列定義の生成に必要な画面状態 */
type Params = {
  tc: TranslateFn;
  permission: OrderDetailPermission;
  readOnly: boolean;
  bemacFlg: boolean;
  inpOrder: OrderDetailFormValues;
  /** 全出荷ボタンフラグ */
  shippingAllFlg: boolean;
  setShippingAllFlg: (flg: boolean) => void;
  /** 明細グリッドの参照 */
  gridRef: RefObject<AgGridReact>;
  /** 行削除ボタン押下（引数は行の key） */
  onClickDeleteButton: (key: number) => void;
};

/**
 * 注文詳細 明細グリッドの列定義
 */
export function buildOrderDetailColumnDefs(params: Params) {
  const {
    tc,
    permission,
    readOnly,
    bemacFlg,
    inpOrder,
    shippingAllFlg,
    setShippingAllFlg,
    gridRef,
    onClickDeleteButton,
  } = params;
  const {
    editableColumn,
    editableReplyColumn,
    editableR3Values,
    editableCancelQuantity,
    showsChangeColor,
  } = permission;

  // 出荷ボタンの表示文言
  function getShippingButtonText(row: OrderDetailFormRowValues) {
    if (row.isExpenses) {
      if (row.confirmFlg) {
        if (row.confirmDateTime) {
          return tc("経費確定済");
        } else {
          return tc("経費確定");
        }
      } else {
        return tc("経費未確定");
      }
    } else if (row.isDeliveryCost) {
      if (row.confirmFlg) {
        if (row.confirmDateTime) {
          return tc("送料確定済");
        } else {
          return tc("送料確定");
        }
      } else {
        return tc("送料未確定");
      }
    } else {
      if (row.confirmFlg) {
        if (row.confirmDateTime) {
          return tc("出荷済");
        } else {
          return tc("出荷対象");
        }
      } else {
        return tc("未出荷");
      }
    }
  }

  // 全出荷ボタンレンダリング
  function rendererShippingAll() {
    //  顧客は非表示
    if (!bemacFlg) {
      return <></>;
    }
    //  完了、否認時は非表示
    if (
      inpOrder.statusClassValue === OrderStatus.COMPLETED ||
      inpOrder.statusClassValue === OrderStatus.REQUESTED_CHANGE
    ) {
      return <></>;
    }

    return (
      <input
        type="button"
        value={tc("全出荷")}
        data-cy="全出荷ボタン"
        className="btn btn-sm text-white"
        style={
          shippingAllFlg
            ? { backgroundColor: "#00b0f0" }
            : { backgroundColor: "#e76300" }
        }
        onClick={() => {
          gridRef.current?.api.forEachNode(
            (node: RowNode<OrderDetailFormRowValues>) => {
              if (!node.data?.confirmDateTime && node.data?.isDbRow) {
                node.setDataValue("confirmFlg", !shippingAllFlg);
              }
            }
          );
          setShippingAllFlg(!shippingAllFlg);
        }}
      />
    );
  }

  // 出荷ボタンレンダリング
  function rendererShipping(
    rowData: ICellRendererParams<OrderDetailFormRowValues>
  ) {
    const row = rowData.data!;
    return (
      <input
        type="button"
        value={getShippingButtonText(row)}
        className="btn btn-sm text-white"
        disabled={
          !bemacFlg ||
          inpOrder.statusClassValue === OrderStatus.COMPLETED ||
          inpOrder.statusClassValue === OrderStatus.REQUESTED_CHANGE ||
          row.confirmDateTime !== undefined ||
          (bemacFlg && !row.isDbRow)
        }
        style={
          row.confirmFlg
            ? { backgroundColor: "#00b0f0" }
            : { backgroundColor: "#e76300" }
        }
        onClick={() => {
          const newValue = !row.confirmFlg;
          if (!newValue) {
            rowData.node.setDataValue("cancelOrderQuantity", undefined);
          }
          rowData.node.setDataValue("confirmFlg", !row.confirmFlg);
        }}
      />
    );
  }

  // 削除ボタンレンダリング
  function rendererDeleteButton(
    rowData: ICellRendererParams<OrderDetailFormRowValues>
  ) {
    return renderDetailDeleteButton(
      rowData,
      permission.canDeleteRow(rowData),
      onClickDeleteButton
    );
  }

  // 空行・「---」行を空表示にする数値フォーマッタ
  const numberFormatter = createDetailNumberFormatter(isEmptyOrderRow);
  const unitCostFormatter = createDetailNumberFormatter(
    isEmptyOrderRow,
    tc("要問合せ")
  );

  return [
    {
      headerName: "",
      children: [
        {
          headerName: "",
          field: "confirmFlg",
          width: isEnglish() ? 225 : 120,
          pinned: true,
          cellRenderer: rendererShipping,
          headerComponent: rendererShippingAll,
          hide: !permission.showsShippingCheckColumn(),
        },
      ],
    },
    {
      // 明細：顧客発注内容
      headerName: tc("明細"),
      children: [
        {
          headerName: "No",
          field: "orderDetailNo",
          width: 90,
          pinned: true,
          rowDrag: permission.canDragRow(),
          cellClassRules: {
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("品目コード"),
          field: "productCode",
          width: 120,
          pinned: true,
          editable: editableColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableColumn),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
      ],
    },
    {
      headerName: "",
      children: [
        {
          headerName: tc("品名"),
          field: "productGroupName",
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: agProductNameFormatter,
        },
        {
          headerName: tc("型式"),
          field: "spec",
          editable: editableColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableColumn),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("単価"),
          width: 100,
          field: "unitCost",
          cellClass: "b-grid-cell-multi-line justify-content-end",
          cellClassRules: {
            "text-start": (params: any) =>
              params.data?.unitCost === null ||
              typeof params.data?.unitCost === "undefined",
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: unitCostFormatter,
        },
        {
          headerName: tc("数量"),
          width: 100,
          colId: "quantity",
          field: "quantity",
          cellStyle: { textAlign: "right" },
          editable: editableColumn,
          valueFormatter: numberFormatter,
          cellClassRules: {
            "b-editable-cell": (
              e: ICellRendererParams<OrderDetailFormRowValues>
            ) =>
              editableColumn(e) &&
              e.data?.productCode !== ProductCodeConst.BLANK &&
              e.data?.existFlg,
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueSetter: (params: ValueSetterParams) =>
            agNumberSetter(params, "quantity"),
        },
        {
          headerName: tc("単位"),
          width: 100,
          colId: "unit",
          field: "unit",
          editable: editableColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableColumn),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("小計"),
          width: 100,
          field: "detailSubTotal",
          cellStyle: { textAlign: "right" },
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: numberFormatter,
        },
        {
          headerName: tc("明細備考"),
          field: "note",
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("削除"),
          colId: "deleteByUser",
          width: isEnglish() ? 90 : 80,
          cellRenderer: rendererDeleteButton,
          hide:
            bemacFlg ||
            readOnly ||
            inpOrder.statusClassValue === OrderStatus.ARRANGING,
          cellClassRules: {
            "b-error-cell": errorCellRule,
          },
        },
      ],
    },
    {
      // 明細：BEMAC回答
      headerName: tc("BEMAC回答"),
      headerClass: "b-ag-column-reply",
      children: [
        {
          headerName: tc("単価"),
          width: 100,
          field: "replyUnitCost",
          cellStyle: { textAlign: "right" },
          valueFormatter: numberFormatter,
          editable: editableReplyColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableReplyColumn),
            "b-ag-column-reply": (params: any) => !editableReplyColumn(params),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          hide:
            !permission.showsReplyColumns() || permission.isGeneralEngineer(),
          headerClass: "b-ag-column-reply",
          valueSetter: (params: ValueSetterParams) =>
            agNumberSetter(params, "replyUnitCost"),
        },
        {
          headerName: tc("数量"),
          width: isEnglish() ? 100 : 90,
          field: "replyQuantity",
          cellStyle: { textAlign: "right" },
          editable: editableReplyColumn,
          valueFormatter: numberFormatter,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableReplyColumn),
            "b-ag-column-reply": (params: any) => !editableReplyColumn(params),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          hide: !permission.showsReplyColumns(),
          headerClass: "b-ag-column-reply",
          valueSetter: (params: ValueSetterParams) =>
            agNumberSetter(params, "replyQuantity"),
        },
        {
          headerName: tc("小計"),
          width: 100,
          field: "replyDetailSubTotal",
          cellClass: "b-ag-column-reply",
          cellStyle: { textAlign: "right" },
          valueFormatter: numberFormatter,
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          hide:
            !permission.showsReplyColumns() || permission.isGeneralEngineer(),
          headerClass: "b-ag-column-reply",
        },
        // R3関連（アシスタントユーザ入力）
        {
          headerName: "Note 1",
          field: "r3WbsLv2",
          width: 150,
          editable: editableR3Values,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableR3Values),
            "b-ag-column-reply": (params: any) => !editableR3Values(),
            "b-error-cell": errorCellRule,
          },
          hide: !permission.showsR3Values(),
          headerClass: "b-ag-column-reply",
        },
        {
          headerName: "Note 2",
          field: "r3OrderNo",
          width: 150,
          editable: editableR3Values,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableR3Values),
            "b-ag-column-reply": (params: any) => !editableR3Values(),
            "b-error-cell": errorCellRule,
          },
          hide: !permission.showsR3Values(),
          headerClass: "b-ag-column-reply",
        },
        {
          headerName: "出荷日",
          field: "r3ShippingDate",
          width: 135,
          valueFormatter: agYmdFormatter,
          cellEditor: AgDatePicker,
          editable: editableR3Values,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableR3Values),
            "b-ag-column-reply": (params: any) => !editableR3Values(),
            "b-error-cell": errorCellRule,
          },
          hide: !permission.showsR3Values(),
          headerClass: "b-ag-column-reply",
        },
        {
          headerName: tc("納期"),
          field: "replyDeliveryDate",
          width: 135,
          editable: editableReplyColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableReplyColumn),
            "b-ag-column-reply": (params: any) => !editableReplyColumn(params),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: agYmdFormatter,
          cellEditor: AgDatePicker,
          hide:
            !inpOrder.replyInstallmentDeliveryFlg ||
            !permission.showsReplyColumns(),
          headerClass: "b-ag-column-reply",
        },
        {
          headerName: tc("削除"),
          colId: "deleteByBemac",
          width: isEnglish() ? 90 : 80,
          cellClass: "b-ag-column-reply",
          cellRenderer: rendererDeleteButton,
          hide: !bemacFlg || readOnly,
          headerClass: "b-ag-column-reply",
          cellClassRules: {
            "b-error-cell": errorCellRule,
          },
        },
      ],
    },
    {
      // 明細：出荷後キャンセル
      headerName: tc("出荷後キャンセル"),
      headerClass: "b-ag-column-cancel",
      children: [
        {
          headerName: tc("数量"),
          width: isEnglish() ? 100 : 90,
          field: "cancelOrderQuantity",
          editable: editableCancelQuantity,
          cellStyle: { textAlign: "right" },
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableCancelQuantity),
            "b-ag-column-cancel": (e: any) => !editableCancelQuantity(e),
            "b-error-cell": errorCellRule,
          },
          hide: !permission.showsCancelColumns(),
          headerClass: "b-ag-column-cancel",
          valueSetter: (params: ValueSetterParams) =>
            agNumberSetter(params, "cancelOrderQuantity"),
        },
        {
          headerName: tc("小計"),
          width: 100,
          field: "cancelSubTotal",
          cellStyle: { textAlign: "right" },
          cellClass: "b-ag-column-cancel",
          valueFormatter: agNumberFormatter,
          hide:
            !permission.showsCancelColumns() || permission.isGeneralEngineer(),
          headerClass: "b-ag-column-cancel",
          cellClassRules: {
            "b-error-cell": errorCellRule,
          },
        },
      ],
    },
  ];
}
